/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { appRoutes } from 'app/routes';
import { compileMenu } from 'app/core/utils/menu';
import { Dashboard } from './dashboard';
import { LayoutDashboard, ListOrdered, Repeat, ScrollText, Settings } from 'lucide-react';
import { useCryptoBotConfig } from 'app/hooks/requests/crypto-bot/use-crypto-bot';

/**
 * Export `SkinCryptoBot` component.
 *
 * Section shell for Crypto Bot — an automated Binance trading bot. Fixed
 * sub-menu: Overview, Positions, Settings.
 */

export function SkinCryptoBot(): JSX.Element {
  const [translate] = useTranslation();
  const { data: config } = useCryptoBotConfig();
  const isRebalance = config?.strategy === 'rebalance' || config?.strategy === 'rebalance_multi';
  const items = useMemo(
    () =>
      compileMenu([
        {
          key: 'overview',
          pathname: appRoutes.dashboard.cryptoBot.overview,
          icon: <LayoutDashboard className={'h-4 w-4'} />,
          label: translate('cryptoBot.menu.overview')
        },
        // Rebalance strategies hold assets toward a target weight — there are no
        // discrete positions, so the Positions tab is hidden (holdings show on
        // the overview instead).
        ...(isRebalance
          ? [
              {
                key: 'rebalances',
                pathname: appRoutes.dashboard.cryptoBot.rebalances,
                icon: <Repeat className={'h-4 w-4'} />,
                label: translate('cryptoBot.menu.rebalances')
              }
            ]
          : [
              {
                key: 'positions',
                pathname: appRoutes.dashboard.cryptoBot.positions,
                icon: <ListOrdered className={'h-4 w-4'} />,
                label: translate('cryptoBot.menu.positions')
              }
            ]),
        {
          key: 'logs',
          pathname: appRoutes.dashboard.cryptoBot.logs,
          icon: <ScrollText className={'h-4 w-4'} />,
          label: translate('cryptoBot.menu.logs')
        },
        {
          key: 'settings',
          pathname: appRoutes.dashboard.cryptoBot.settings,
          icon: <Settings className={'h-4 w-4'} />,
          label: translate('cryptoBot.menu.settings')
        }
      ]),
    [translate, isRebalance]
  );

  return (
    <Dashboard menu={items}>
      <Outlet />
    </Dashboard>
  );
}
